import { useDropzone } from 'react-dropzone-14';
import { CircularProgress } from '@mui/material';
import { FormMedia } from '../../model/programForm.types';
import { useMediaUpload } from '../../lib/useMediaUpload';
import * as styles from './MediaDropField.css';

type MediaDropFieldProps = {
  value: FormMedia;
  onChange: (value: FormMedia) => void;
  accept: Record<string, string[]>;
  format: 'image' | 'video';
  disabled?: boolean;
  testId?: string;
};

const buildMediaUrl = (value: NonNullable<FormMedia>): string | null => {
  const cdn = value.cdn01 ?? '';
  if (value.video) return `${cdn}${value.video}`;
  if (value.image) return `${cdn}${value.image}`;
  return null;
};

export const MediaDropField = ({
  value,
  onChange,
  accept,
  format,
  disabled,
  testId,
}: MediaDropFieldProps) => {
  const { isUploading, error, upload, setError } = useMediaUpload(onChange);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept,
    multiple: false,
    disabled: disabled || isUploading,
    noClick: true,
    onDrop: (files) => {
      const file = files[0];
      if (file) upload(file);
    },
    onDropRejected: () => {
      setError(`Unsupported file. Please choose a ${format} file.`);
    },
  });

  if (value && !isUploading) {
    const url = buildMediaUrl(value);
    const isVideo = format === 'video' || Boolean(value.video);
    return (
      <div className={styles.container} data-testid={testId}>
        {url ? (
          isVideo ? (
            <video className={styles.preview} src={url} controls />
          ) : (
            <img className={styles.preview} src={url} alt="" />
          )
        ) : null}
        <button
          type="button"
          className={styles.removeButton}
          onClick={() => onChange(null)}
          disabled={disabled}
          aria-label={`Remove ${format}`}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      {...getRootProps({ className: styles.dropzone })}
      data-testid={testId}
      data-drag-active={isDragActive}
    >
      <input {...getInputProps()} />
      <div className={styles.message}>
        {isUploading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            Drag &amp; drop {format} here or{' '}
            <button
              type="button"
              className={styles.chooseLink}
              onClick={open}
              disabled={disabled}
            >
              Choose files
            </button>
            {error ? <div className={styles.error}>{error}</div> : null}
          </>
        )}
      </div>
    </div>
  );
};
